import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDebouncedCallback } from 'use-debounce';

import BaseLink from '~/components/base/BaseLink';
import Icon from '~/components/base/Icon';
import MobileNavChildMenu from '~/components/footer/MobileNavChildMenu';

import { mobileOnly } from '~/styles/helpers';
import { transition } from '~/styles/transition';

import useWindowSize from '~/hooks/useWindowSize';

import { useUser } from '~/context/UserContext';
import { useCart } from '~/context/CartContext';

import { srOnly, notSrOnly } from '~/styles/helpers';

import { formatText } from '~/utilities/helpers';
import { graphql, useStaticQuery } from 'gatsby';
import { isEmpty } from 'lodash';
import trackCustomEvent from '~/utilities/trackCustomEvent';

const Wrapper = styled.div`
    display: flex;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        justify-content: flex-end;
        flex: 1 1 20%;
    }
`;

const AccountDropdown = styled.div`
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    background-color: ${({ theme }) => theme.colors.accent.blue};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.7rem;
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    padding: 10px 0 22px 0;

    &:before {
        position: absolute;
        content: '';
        width: 100%;
        height: 20px;
        top: -20px;
        z-index: 3;
    }

    &:hover,
    &:focus {
        display: block;
    }

    h3 {
        padding: 20px;
        margin: 0;
        font-weight: ${({ theme }) => theme.fontWeights.black};
    }
`;

const CartCount = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-100%);
    right: 0;
    height: 20px;
    width: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary.white};
    font-size: 1.3rem;
    color: ${({ theme }) => theme.colors.text.blue};
    ${transition({}, 'color')};

    span {
        margin-top: 2px;
    }
`;

export const NavIconLink = styled(BaseLink)`
    ${transition({}, 'background-color')};
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 0;
    font-size: 2.4rem;
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.black};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 10px;
        gap: 5px;
        font-size: 1.4rem;
        height: 100%;

        &:hover,
        &:focus {
            background-color: ${({ theme }) => theme.colors.accent.blue};

            ${CartCount} {
                color: ${({ theme }) => theme.colors.accent.blue};
            }
        }

        ${({ theme, isDropdownOpen }) =>
            isDropdownOpen && `background-color: ${theme.colors.accent.blue}`}

        > span {
            ${srOnly};
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding: 30px 8px;
        > span {
            ${notSrOnly};
            line-height: 1;
            white-space: nowrap;
        }
    }

    @media (min-width: 1400px) {
        padding: 32px 10px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        font-size: 1.8rem;
        padding: 30px 10px;
    }
`;

const NavIconButton = styled.button`
    ${transition({}, 'background-color')};
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 0;
    font-size: 2.4rem;
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.black};
`;

const MobileOnlySpan = styled.span`
    ${mobileOnly};

    ${({ isLoggedIn, theme }) =>
        !isLoggedIn
            ? `
    @media (min-width: ${theme.breakpoint.largeTablet}) {
      position: absolute !important;
      top: calc(100% + 10px);
      padding: 5px 10px;
      right: 50%;
      transform: translateX(50%);
      overflow: visible !important;
      font-size: 1.4rem;
      background-color: ${theme.colors.accent.blue};
      
      &:before {
        position: absolute;
        content: '';
        width: 0;
        height: 0;
        border-left: 15px solid transparent;
        border-right: 15px solid transparent;
        border-bottom: 15px solid ${theme.colors.accent.blue};
        top: -10px;
        right: 50%;
        transform: translateX(50%);
      }
    }
    `
            : ''};
`;

const IconLinkList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 20px;

    > li {
        &:hover,
        &:focus-within {
            ${NavIconLink} {
                background-color: ${({ theme }) => theme.colors.accent.blue};
            }

            ${AccountDropdown} {
                display: block;
            }

            ${MobileOnlySpan} {
                @media (min-width: ${({ theme }) =>
                        theme.breakpoint.largeTablet}) {
                    ${({ isLoggedIn }) => !isLoggedIn && `display: block;`}
                }
            }
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        flex-direction: row;
        gap: 0;
        height: 100%;

        li {
            position: relative;
        }
    }
`;

const ShoppingCart = styled.li`
    display: none;
    position: relative;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: list-item;
    }
`;

const ItemsAdded = styled.div`
    ${transition({ speed: 1000 }, 'opacity', 'visibility')};
    position: absolute;
    top: 100%;
    right: 0;
    background-color: ${({ theme }) => theme.colors.accent.blue};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.7rem;
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    opacity: ${({ showItemsAdded }) => (showItemsAdded ? `1` : `0`)};
    visibility: ${({ showItemsAdded }) =>
        showItemsAdded ? `visible` : `hidden`};
`;

const ItemAdded = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const AccountLink = styled(BaseLink)`
    display: block;
    padding: 8px 20px;
    text-decoration: underline;
    ${transition({}, 'color')};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.primary.yellow};
    }
`;

const FixedNav = ({ simplifiedLayout = false, triggerRef, links }) => {
    const {
        wpMenu: { menuItems }
    } = useStaticQuery(graphql`
        query UserAccountMenu {
            wpMenu(locations: { eq: USER_ACCOUNT_MENU }) {
                menuItems {
                    nodes {
                        label
                        url
                        id
                        childItems {
                            nodes {
                                label
                                url
                                id
                            }
                        }
                    }
                }
            }
        }
    `);
    const { user, isLoggedIn } = useUser();
    const { items, itemsUpdated, newItem, toggleItemsUpdated } = useCart();
    const [showItemsAdded, setShowItemsAdded] = useState(false);
    const [mobileAccountMenuOpen, setMobileAccountMenuOpen] = useState(false);

    const headerRef = useRef(null);

    const windowSizes = useWindowSize();

    const accountDropdownRef = useRef(null);

    const debounceNotification = useDebouncedCallback(() => {
        toggleItemsUpdated();
        setShowItemsAdded(false);
    }, 2500);

    const handleAccountMenu = () => {
        setMobileAccountMenuOpen(!mobileAccountMenuOpen);
        if (headerRef && headerRef.current) {
            headerRef.current.focus();
        }
    };

    useEffect(() => {
        if (itemsUpdated && !isEmpty(newItem) && !showItemsAdded) {
            setShowItemsAdded(true);
            debounceNotification();
        }
    }, [
        itemsUpdated,
        newItem,
        toggleItemsUpdated,
        showItemsAdded,
        debounceNotification
    ]);

    return (
        <Wrapper>
            <IconLinkList {...{ isLoggedIn }}>
                {!simplifiedLayout &&
                    links.map(({ link, loggedInLink, icon }) => (
                        <li key={link.title}>
                            <NavIconLink
                                to={
                                    loggedInLink && isLoggedIn
                                        ? loggedInLink.url
                                        : link.url
                                }
                                onClick={() =>
                                    trackCustomEvent('main_navigation_click', {
                                        value:
                                            loggedInLink && isLoggedIn
                                                ? loggedInLink.url
                                                : link.url
                                    })
                                }
                            >
                                <Icon
                                    icon={icon}
                                    sizeLarge={27}
                                    sizeTablet={20}
                                    sizeMobile={27}
                                    color="yellow"
                                />
                                <span>
                                    {loggedInLink && isLoggedIn
                                        ? formatText(loggedInLink.title)
                                        : formatText(link.title)}
                                </span>
                            </NavIconLink>
                        </li>
                    ))}
                <li ref={accountDropdownRef}>
                    {windowSizes.includes('largeTablet') || !isLoggedIn ? (
                        <NavIconLink to="/account">
                            <Icon
                                icon="user"
                                sizeLarge={27}
                                sizeTablet={20}
                                sizeMobile={27}
                                color="yellow"
                            />
                            <MobileOnlySpan {...{ isLoggedIn }}>
                                My Account
                            </MobileOnlySpan>
                        </NavIconLink>
                    ) : (
                        <NavIconButton
                            onClick={() => setMobileAccountMenuOpen(true)}
                            ref={headerRef}
                        >
                            <Icon
                                icon="user"
                                sizeLarge={27}
                                sizeTablet={20}
                                sizeMobile={27}
                                color="yellow"
                            />
                            <MobileOnlySpan {...{ isLoggedIn }}>
                                My Account
                            </MobileOnlySpan>
                        </NavIconButton>
                    )}
                    {isLoggedIn ? (
                        windowSizes.includes('largeTablet') ? (
                            <AccountDropdown id="account-dropdown">
                                <h3>Welcome, {user.name}!</h3>
                                <ul>
                                    {menuItems.nodes.map((menuItem) => {
                                        return (
                                            <li key={menuItem.id}>
                                                <AccountLink to={menuItem.url}>
                                                    {menuItem.label}
                                                </AccountLink>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </AccountDropdown>
                        ) : (
                            mobileAccountMenuOpen && (
                                <MobileNavChildMenu
                                    label="My Account"
                                    childItems={menuItems}
                                    url="/account"
                                    handleMenu={handleAccountMenu}
                                    {...{ triggerRef, headerRef }}
                                />
                            )
                        )
                    ) : null}
                </li>
                {isLoggedIn && !windowSizes.includes('largeTablet') && (
                    <li>
                        <NavIconLink to="/account/logout">
                            <Icon
                                icon="arrow"
                                sizeLarge={27}
                                sizeTablet={20}
                                sizeMobile={27}
                                color="yellow"
                            />
                            Logout
                        </NavIconLink>
                    </li>
                )}

                <ShoppingCart>
                    <NavIconLink to="/cart">
                        <Icon
                            icon="shopping-bag"
                            sizeLarge={27}
                            sizeTablet={20}
                            sizeMobile={27}
                            color="yellow"
                        />
                        {items && items.length ? (
                            <CartCount>
                                <span>{items.length}</span>
                            </CartCount>
                        ) : null}
                    </NavIconLink>
                    <ItemsAdded {...{ showItemsAdded }}>
                        {newItem.title ? (
                            <ItemAdded>
                                <Icon
                                    icon="checkmark"
                                    sizeLarge={27}
                                    sizeTablet={20}
                                    sizeMobile={27}
                                    color="yellow"
                                />
                                <span>
                                    {formatText(newItem.title)} added to cart
                                </span>
                            </ItemAdded>
                        ) : null}
                        {newItem.addOns?.length
                            ? newItem.addOns.map(({ title, id }) => (
                                  <ItemAdded key={id}>
                                      <Icon
                                          icon="checkmark"
                                          sizeLarge={27}
                                          sizeTablet={20}
                                          sizeMobile={27}
                                          color="yellow"
                                      />
                                      <span>
                                          {formatText(title)} added to cart
                                      </span>
                                  </ItemAdded>
                              ))
                            : null}
                    </ItemsAdded>
                </ShoppingCart>
            </IconLinkList>
        </Wrapper>
    );
};

FixedNav.propTypes = {
    simplifiedLayout: PropTypes.bool,
    triggerRef: PropTypes.shape({}),
    links: PropTypes.arrayOf(
        PropTypes.shape({
            link: PropTypes.shape({
                url: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                target: PropTypes.string
            }).isRequired,
            loggedInLink: PropTypes.shape({
                url: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                target: PropTypes.string
            }),
            icon: PropTypes.string.isRequired
        })
    ).isRequired
};

export default FixedNav;
